import { allOf, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The lease renewal follow-up: check in on a resident's renewal plans with two
 * quick questions, then answer theirs from the knowledge base. The agent does
 * not negotiate rent, terms or dates. It cannot transfer the call, so a
 * request for a person goes to its own node, which takes a message.
 */
const START_ID = '00000000-0000-4000-8000-00000005d001';
const GREET_ID = '00000000-0000-4000-8000-00000005a001';
const PLANS_ID = '00000000-0000-4000-8000-00000005c001';
const MESSAGE_ID = '00000000-0000-4000-8000-00000005c002';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000005a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000005a007';
const edgeId = (n) => `00000000-0000-4000-8000-00000005e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(8),
    toNodeId: GREET_ID,
});
export const LEASE_RENEWAL_GRAPH_TEMPLATE = {
    name: 'Lease renewal',
    persona: 'You are Sam, a friendly assistant for Super. One or two short spoken ' +
        'sentences per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi {{firstName}}, this is Sam with Super. Your lease renewal is ' +
                'ready and waiting for you, so I wanted to quickly check in on your ' +
                'plans before the deadline.',
        },
        {
            id: PLANS_ID,
            type: 'conversation',
            title: 'Renewal plans',
            position: 2,
            prompt: 'Ask these two questions, one at a time, and acknowledge each ' +
                'answer:\n\n' +
                '1. "Are you planning to renew, or are you thinking about moving ' +
                'out?"\n\n' +
                '2. If they are renewing: "Great! Please sign the offer in your ' +
                'tenant portal, before the deadline, which is end of this month" If ' +
                'they are moving out: "Got it. Please submit a notice to vacate by ' +
                'the deadline, which is end of this month"\n\n' +
                'Then ask if they have any questions about the renewal, and answer ' +
                'from the knowledge base. Never negotiate rent, terms or dates; say ' +
                'a team member will follow up.\n\n' +
                'After each answer, ask if there is anything else you can help ' +
                'answer.',
            tools: [{ tool: 'SWITCH_LANGUAGE' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'ready_to_wrap_up',
                    text: 'The caller indicates they have no further questions or they are ' +
                        'ready to move on or wrap up the conversation.',
                },
            ],
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 3,
            prompt: 'Say you cannot transfer them right now, but you can take a ' +
                'message for the team to follow up. Take down their message and ' +
                'read it back briefly. Then ask: "Is there anything else I can help ' +
                'with?"',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Thanks, {{firstName}}! I'll pass your plans along to the team. Have " +
                    'a great day!',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        { ...OPENING.edges[1], position: 3 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: PLANS_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 4,
        },
        {
            id: edgeId(5),
            fromNodeId: PLANS_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(PLANS_ID, 'ready_to_wrap_up', true)),
            position: 5,
        },
        {
            id: edgeId(7),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 6,
        },
    ],
};
//# sourceMappingURL=lease-renewal-template.js.map